/*
 * Select.tsx
 *
 * custom drop-down selection component used to select security questions from a list of security questions.
 *
 * Created by Nick Perry on 9/2/22.
 *
 * dha-login
 *
 * Copyright © 2009-2022 United States Government as represented by
 * the Program Manager of the DHA: Web and Mobile Technology Program Management Office.
 * All Rights Reserved.
 *
 * Copyright © 2009-2022 Contributors. All Rights Reserved.
 *
 * THIS OPEN SOURCE AGREEMENT ("AGREEMENT") DEFINES THE RIGHTS OF USE,
 * REPRODUCTION, DISTRIBUTION, MODIFICATION AND REDISTRIBUTION OF CERTAIN
 * COMPUTER SOFTWARE ORIGINALLY RELEASED BY THE UNITED STATES GOVERNMENT
 * AS REPRESENTED BY THE GOVERNMENT AGENCY LISTED BELOW ("GOVERNMENT AGENCY").
 * THE UNITED STATES GOVERNMENT, AS REPRESENTED BY GOVERNMENT AGENCY, IS AN
 * INTENDED THIRD-PARTY BENEFICIARY OF ALL SUBSEQUENT DISTRIBUTIONS OR
 * REDISTRIBUTIONS OF THE SUBJECT SOFTWARE. ANYONE WHO USES, REPRODUCES,
 * DISTRIBUTES, MODIFIES OR REDISTRIBUTES THE SUBJECT SOFTWARE, AS DEFINED
 * HEREIN, OR ANY PART THEREOF, IS, BY THAT ACTION, ACCEPTING IN FULL THE
 * RESPONSIBILITIES AND OBLIGATIONS CONTAINED IN THIS AGREEMENT.
 *
 * Government Agency: DHA: Web and Mobile Technology Program Management Office
 * Government Agency Original Software Designation: dha-login
 * Government Agency Original Software Title: dha-login
 * User Registration Requested. Please send email
 * with your contact information to: robert.a.kayl.civ@mail.mil
 * Government Agency Point of Contact for Original Software - Program Manager: robert.a.kayl.civ@mail.mil
 */

import { Fragment } from 'react';

import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import type { FieldHookConfig } from 'formik';
import { useField } from 'formik';
import { useAtomValue } from 'jotai';

import { themeAtom } from '../../../atoms/themeAtom';

const questions = [
  'In what city did you meet your first spouse/partner?',
  'In what city did your parents meet?',
  'In what city does your nearest sibling live?',
  'In what city was your father born?',
  'In what city was your first job?',
  'In what city was your mother born?',
  "What city were you in on New Year's 2000?",
  'What is the last name of your favorite teacher in high school?',
  "What is the name of a college you applied to but didn't attend?",
  "What is your father's middle name?",
  'What is your favorite food?',
  "What is your maternal grandmother's first and last name?",
  "What is your mother's middle name?",
  "What is your oldest sibling's middle name?",
  "What is your paternal grandfather's first and last name?",
  "What is your youngest sibling's middle name?",
  'What school did you attend for sixth grade?',
  'What was the first and last name of your childhood best friend?',
  'What was the first and last name of your first significant other?',
  'What was the last name of your favorite grade school teacher?',
  'What was the make and model of your first car or motorcycle?',
  'What was the name of the first school you attended?',
  'What was the name of the hospital in which you were born?',
  'What was the name of the street of your first childhood home?',
  'What was the name of your childhood hero?',
  'What was the name of your favorite stuffed animal?',
  'What was the name of your first pet?',
  'What was your childhood nickname?',
  'What was your favorite sport in high school?',
  'What was your first job?',
  'What were the last four digits of your childhood telephone number?',
  'When you were young, what did you want to be when you grew up?',
  'Who is the most famous person you have ever met?',
];

interface SelectProps {
  peerQuestion: string;
}

const Select = (props: SelectProps & FieldHookConfig<string>) => {
  // useField comes from formik and allows easier state management for more complex input structures (such as this Listbox dropdown component from HeadlessUI that does not use the native HTML dropdown element)
  //   if you need to know more about how useField works, the official Formik docs are very informative.
  const [field] = useField(props);
  // A Select component (dropdown component) gets passed a peerQuestion, allowing it to know what question has already been chosen.
  const { name, peerQuestion } = props;

  const theme = useAtomValue(themeAtom);

  // function that returns an array of all questions not currently selected as a chosenQuestion
  //   this prevents the user from choosing the same security question twice.
  const createQuestionList = (chosenQuestion: string) => {
    const validQuestions = questions.map((question, questionIndx) => {
      if (question !== chosenQuestion) {
        return (
          <Listbox.Option
            key={questionIndx}
            className={({ active }) =>
              `relative cursor-default select-none border py-2 pl-10 pr-4 md:border-hidden ${
                active ? 'bg-sky-300/20 text-sky-800' : 'text-gray-900'
              }`
            }
            value={question}
          >
            {({ selected }) => (
              <>
                <span className={`block ${selected ? 'font-medium' : 'font-normal'}`}>
                  {question}
                </span>
                {selected ? (
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-400">
                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                ) : null}
              </>
            )}
          </Listbox.Option>
        );
      } else {
        return null;
      }
    });

    return validQuestions;
  };
  /*
  HeadlessUI Listbox component structure:
  <Listbox>
    <Listbox.Button>holds clickable dropdown button</Listbox.Button>
    <Listbox.Options>
      <Listbox.Option>holds individual selectable security question element</ListboxOption>
      <Listbox.Option>holds individual selectable security question element</ListboxOption>
      <Listbox.Option>holds individual selectable security question element</ListboxOption>
      ...
    </Listbox.Options>
  </Listbox>
   */
  return (
    <Listbox
      value={field.value}
      onChange={(value: string) => {
        field.onChange({ target: { value, name } });
      }}
    >
      <div className="relative mt-1">
        <Listbox.Button
          className={`relative w-full ${theme.dropDownBorder} cursor-default rounded-lg !bg-white py-2 pl-3 pr-10 text-left text-sm shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-dha-light-blue sm:text-sm`}
        >
          <span className="block ">{field.value || 'Please select a question'}</span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </span>
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-left text-sm shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
            {/* The purpose of createQuestionList() is to render every question as an option EXCEPT for any question already chosen as a security question (peerQuestion prop). */}
            {createQuestionList(peerQuestion)}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
};

export default Select;
